import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  FilterSchema,
  ParagraafSchemaObjectStatus,
  SubparagraafSchemaObjectStatus,
  VoorschriftSchemaObjectStatus,
} from '@/api/generated/digiVAPI.schemas';
import { useLinksInHoofdstuk } from '@/api/queries/objects';
import { createInString } from '@/utils/createInString';

import { EditorView } from '../-store/useEditorStore';
import { LabelEditorFilter } from '../-store/useLabelEditorStore';

type UseEditorLinksProps = {
  hoofdstukId: string;
  besluitId: string;
  view: EditorView;
  labelFilter?: LabelEditorFilter;
};

export const getEditorLinksQueryOptions = ({
  hoofdstukId,
  besluitId,
  view,
  labelFilter,
}: UseEditorLinksProps) => {
  const VSPBFilter: FilterSchema[] = [
    { model: 'Voorschrift', field: 'Volgend', op: 'is_null', value: '' },
  ];

  const SPPBFilter: FilterSchema[] = [
    { model: 'Subparagraaf', field: 'Volgend', op: 'is_null', value: '' },
  ];

  const PHBFilter: FilterSchema[] = [
    { model: 'Paragraaf', field: 'Volgend', op: 'is_null', value: '' },
  ];

  if (labelFilter?.type === 'labels') {
    VSPBFilter.push({
      model: 'Label',
      field: 'ID',
      op: 'in',
      value: createInString(labelFilter.labelIds),
    });
  }

  if (labelFilter?.type === 'showAllWithoutLabel') {
    VSPBFilter.push({
      model: 'Voorschrift',
      field: 'nolabel',
      op: 'eq',
      value: 'true',
    });
  }

  if (view !== EditorView.RECOVER) {
    VSPBFilter.push({
      model: 'Voorschrift',
      field: 'ObjectStatus',
      op: 'ne',
      value: VoorschriftSchemaObjectStatus.IngetrokkenGepubliceerd,
    });

    SPPBFilter.push({
      model: 'Subparagraaf',
      field: 'ObjectStatus',
      op: 'ne',
      value: SubparagraafSchemaObjectStatus.IngetrokkenGepubliceerd,
    });

    PHBFilter.push({
      model: 'Paragraaf',
      field: 'ObjectStatus',
      op: 'ne',
      value: ParagraafSchemaObjectStatus.IngetrokkenGepubliceerd,
    });
  }

  return useLinksInHoofdstuk.getOptions(
    {
      hoofdstukId,
      besluitId,
      VSPBFilter,
      SPPBFilter,
      PHBFilter,
    },
    {
      enabled: !!hoofdstukId && !!besluitId,
      select: labelFilter
        ? (links) => {
            if (!links) return [];

            return links
              .map((paragraafLink) => ({
                ...paragraafLink,
                subparagrafen: paragraafLink.subparagrafen.filter(
                  (subparagraafLink) =>
                    subparagraafLink.voorschriften.length > 0
                ),
              }))
              .filter(
                (paragraafLink) => paragraafLink.subparagrafen.length > 0
              );
          }
        : undefined,
    }
  );
};

export const useEditorLinks = ({
  hoofdstukId,
  besluitId,
  view,
  labelFilter,
}: UseEditorLinksProps) => {
  const options = useMemo(() => {
    return getEditorLinksQueryOptions({
      hoofdstukId,
      besluitId,
      view,
      labelFilter,
    });
  }, [hoofdstukId, besluitId, view, labelFilter]);

  return useQuery(options);
};

useEditorLinks.getOptions = getEditorLinksQueryOptions;

export const useEditorLinksSuspense = ({
  hoofdstukId,
  besluitId,
  view,
  labelFilter,
}: UseEditorLinksProps) => {
  const options = useMemo(() => {
    return getEditorLinksQueryOptions({
      hoofdstukId,
      besluitId,
      view,
      labelFilter,
    });
  }, [hoofdstukId, besluitId, view, labelFilter]);

  return useSuspenseQuery(options);
};
